import {
  DEFAULT_SAMPLE_INTERVAL_MS,
  minSpanForSampleIntervalMs,
} from './timeRangeClamp'

/**
 * Surfacing the zoom floor to the user.
 *
 * `clampTimeRange` quietly expands any zoom gesture below the data-derived
 * floor (`minSpanForSampleIntervalMs`: the narrowest window worth charting at
 * the data's measured cadence). That expansion is deliberate — refusing the
 * gesture would be worse — but it is invisible: the user drags to zoom, nothing
 * gets closer, and there is no explanation. These helpers let the bottom bar
 * SAY the window is at the floor without restricting any movement (pan,
 * zoom-out, and presets all still work; further zoom-in simply re-centers).
 */

/**
 * Tolerance multiplier for "is this span AT the floor".
 *
 * A clamped commit lands exactly on the floor (modulo whole-ms rounding), but
 * the floor itself moves when a load refines the cadence estimate — e.g. the
 * zoomed window's data comes back at a finer cadence and the floor drops, at
 * which point the window genuinely can be zoomed further and the indicator
 * should disappear. 1% covers rounding without swallowing that real movement.
 */
const AT_FLOOR_TOLERANCE = 1.01

/**
 * Whether a committed window span sits at the zoom floor for the given cadence.
 *
 * `cadenceMs` is the estimate from `estimateSampleIntervalMs` (null when the
 * data cannot support one — the documented PI default is assumed, matching what
 * the clamp itself does with an unknown cadence).
 */
export const isSpanAtMaxZoom = (
  spanMs: number,
  cadenceMs: number | null
): boolean => {
  if (!Number.isFinite(spanMs) || spanMs <= 0) return false
  return spanMs <= minSpanForSampleIntervalMs(cadenceMs) * AT_FLOOR_TOLERANCE
}

/**
 * Human spelling of a sample cadence for the max-zoom tooltip, e.g. "~60s",
 * "~5m", "~2h". Rounded to the nearest whole unit — this labels an estimate
 * (a median of gaps), so more precision would be false precision.
 */
export const formatSampleCadence = (cadenceMs: number | null): string => {
  const ms =
    cadenceMs !== null && Number.isFinite(cadenceMs) && cadenceMs > 0
      ? cadenceMs
      : DEFAULT_SAMPLE_INTERVAL_MS
  const seconds = ms / 1000
  if (seconds < 120) return `~${Math.round(seconds)}s`
  const minutes = seconds / 60
  if (minutes < 120) return `~${Math.round(minutes)}m`
  return `~${Math.round(minutes / 60)}h`
}
